// Anchor id helpers for the sectioned vault. The middle column tags each
// section header / agent card with `data-anchor`; the sidebar drives smooth
// scroll-into-view by writing the same anchor string into a piece of state
// the parent page owns. Keeping the (de)serialiser pure makes it easy to
// unit-test and swap the format later.

export type Anchor =
  | { kind: 'section'; id: string }
  | { kind: 'agent'; id: string };

export function sectionAnchor(id: string): string {
  return `section-${id}`;
}

export function agentAnchor(id: string): string {
  return `agent-${id}`;
}

/**
 * The Organizations section's anchor, shared by the one-scroll body's
 * `data-anchor` block (AgentsPage) and the sidebar row that scrolls to it —
 * one definition so the two cannot drift apart on a typo. Older sections
 * inline their anchor strings; this one is shared because its surface spans
 * more files (section, switcher, roster).
 */
export const ORGS_ANCHOR = sectionAnchor('orgs');

export function parseAnchor(s: string): Anchor | null {
  if (s.startsWith('section-')) return { kind: 'section', id: s.slice('section-'.length) };
  if (s.startsWith('agent-')) return { kind: 'agent', id: s.slice('agent-'.length) };
  return null;
}

// Normalise an `?anchor=` value into an internal anchor string. Sidebar
// navigation already writes prefixed anchors (`agent-…` / `section-…`), but an
// external deep-link (e.g. a marketplace "open in vault" link) passes the bare
// agentId. When the raw value matches a known agentId we promote it to the
// agent anchor so the scroll/expand path treats it like a sidebar click;
// anything already prefixed (or unrecognised) is returned unchanged. agentIds
// are `<hex>:slug` and never start with `agent-`/`section-`, so there's no
// collision with the prefixed forms.
export function resolveAnchor(raw: string, agentIds: string[]): string {
  if (agentIds.includes(raw)) return agentAnchor(raw);
  return raw;
}
